<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import dayjs from "dayjs";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import ReorderTripStop from "@/views/trips/edit/components/ReorderTripStop.vue";
import { useOrder } from "@/composables";
import type { Trip, TripOrder, TripStop } from "@/types/trip";
import { API } from "@/api/api";
import { useRoute } from "vue-router";
import TripManager from "@/views/trips/edit/services/tripManager/tripManager";

const model = defineModel<boolean>({ required: true });

const api = new API();
const route = useRoute();

const tripManager = new TripManager(api, route);

const props = withDefaults(
  defineProps<{
    trip: Trip;
    newOrder?: TripOrder | null;
  }>(),
  {
    newOrder: null,
  },
);

const emit = defineEmits<{
  (e: "reorder", value: TripStop[]): void;
}>();

const { orderColorList } = useOrder();

const orderColors = ref<Record<string, string>>({});
const tripStops = ref<TripStop[]>([]);

const orders = computed(() => {
  return props.trip.orders;
});
const routes = computed(() => props.trip.routes);
const routeStops = computed(() => routes.value.flatMap((route) => route.routeStops));
const orderFreights = computed(() => orders.value.flatMap((order) => order.freights));
const travelOrders = computed(() => routes.value.flatMap((route) => route.travelOrders));
const hasReload = computed(() => tripStops.value.some((tripStop) => tripStop.isReload));
const hasInvalidDateSequence = computed(() =>
  tripStops.value.some((stop, index, arr) => {
    if (index === 0) {
      return false;
    }

    const prev = dayjs(arr[index - 1].timeTo);
    const current = dayjs(stop.timeTo);

    return current.isBefore(prev);
  }),
);
const isPrimaryButtonDisabled = computed(() => hasInvalidDateSequence.value);

function createTripStops() {
  tripStops.value = tripManager.buildReorderedTripStops(props.trip, props.newOrder);
}

function createOrderColors() {
  orderColors.value = props.trip.orders.reduce<Record<string, string>>((acc, order, index) => {
    if (order.id) {
      acc[order.id] = orderColorList[index];
    }
    return acc;
  }, {});
}

function swapTripStopsPosition(firstStopId: string, secondStopId: string) {
  const first = tripStops.value.find((s) => s.id === firstStopId);
  const second = tripStops.value.find((s) => s.id === secondStopId);

  if (!first || !second) {
    return;
  }

  tripStops.value = [...tripStops.value]
    .map((stop) => {
      if (stop.id === firstStopId) {
        return { ...stop, position: second.position };
      }

      if (stop.id === secondStopId) {
        return { ...stop, position: first.position };
      }

      return stop;
    })
    .sort((a, b) => a.position - b.position);

  tripStops.value = tripManager.recalculateTripStopsWithNumberAndName(tripStops.value);
}

function move(stop: TripStop, direction: number) {
  const index = tripStops.value.findIndex((s) => s.id === stop.id);

  if (index === -1) return;

  const targetIndex = index + direction;

  if (targetIndex < 0 || targetIndex >= tripStops.value.length) {
    return;
  }

  const targetStop = tripStops.value[targetIndex];

  swapTripStopsPosition(stop.id, targetStop.id);
}

function closeReorderStopsDialog() {
  model.value = false;
  emit("reorder", tripStops.value);
}

function onTripStopSave(updatedStop: TripStop) {
  tripStops.value = tripStops.value.map((stop) => {
    if (stop.id === updatedStop.id) {
      return updatedStop;
    }

    return stop;
  });
}

onMounted(() => {
  createOrderColors();
  createTripStops();
});
</script>

<template>
  <omni-dialog
    v-model="model"
    :primary-button-disabled="isPrimaryButtonDisabled"
    hide-close
    persistent
    primary-action-label="Save"
    title="Stops order"
    width="750"
    @primary-action:click="closeReorderStopsDialog"
  >
    <div class="trip-stop__info d-flex align-center flex-wrap mb-6">
      <div v-for="order in orders" :key="order.id" class="trip-stop__info-block d-flex align-center mr-4">
        <span :class="'square-' + orderColors[order.id!]" class="trip-stop__info-color rounded mr-2 d-block" />
        <span class="text-grey-darken-3 font-size-12 font-weight-medium flex-none"> Order #{{ order.number }} </span>
      </div>

      <div v-if="hasReload" class="trip-stop__info-block d-flex align-center mr-4">
        <span class="trip-stop__info-color rounded mr-2 d-block square-reload" />
        <span class="text-grey-darken-3 font-size-12 font-weight-medium flex-none"> Reload stops </span>
      </div>
    </div>
    <div class="trip-stop__blocks">
      <reorder-trip-stop
        v-for="item in tripStops"
        :key="item.id"
        :order-freights="orderFreights"
        :orders="orders"
        :route-stops="routeStops"
        :travel-orders="travelOrders"
        :trip-stop="item"
        :trip-stops="tripStops"
        @move="move"
        @save="onTripStopSave"
      />
    </div>
  </omni-dialog>
</template>

<style lang="scss">
.trip-stop {
  &__info-color {
    width: 16px;
    height: 16px;
  }

  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.square {
  &-primary {
    background-color: #536dfe !important;
  }

  &-success {
    background-color: #7cb342 !important;
  }

  &-warning {
    background-color: #ffab40 !important;
  }

  &-info {
    background-color: #ab47bc !important;
  }

  &-danger {
    background-color: #ff7043 !important;
  }

  &-secondary {
    background-color: #78909c !important;
  }

  &-light {
    background-color: #eceff1 !important;
  }

  &-dark {
    background-color: #37474f !important;
  }

  &-purple {
    background-color: #8e24aa !important;
  }

  &-teal {
    background-color: #26a69a !important;
  }

  &-reload {
    background-color: #ff9800 !important;
  }
}
</style>
